/**
 * React Query hooks for the expert generation-internals viewer:
 * attempt-partitioned timeline + per-step feedback.
 *
 * All hooks are lazy — callers pass `enabled` (panel open && expert) so
 * regular users and collapsed panels never hit the expert-gated endpoints.
 *
 * See also:
 * - ../services/blogCreationService.ts (getGenerationTimeline)
 * - ../services/generationFeedbackService.ts
 * - ../components/BlogCreationCommandCenter/ExpertGenerationTimeline.tsx
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { blogCreationService } from '../services/blogCreationService';
import {
	generationFeedbackService,
	type SubmitStepFeedbackData,
} from '../services/generationFeedbackService';
import { useTenantStore } from '../stores/useTenantStore';
import type { StepFeedback } from '../components/BlogCreationCommandCenter/types';

const timelineKey = (contentId: string | null) => ['generation-timeline', contentId];
const feedbackKey = (contentId: string | null) => ['generation-feedback', contentId];

// Expert-only attempt timeline. Lazy: no polling, fetched only while the
// internals panel is open (caller controls `enabled`).
export const useGenerationTimeline = (
	contentId: string | null,
	options?: { enabled?: boolean },
) => {
	const currentTenant = useTenantStore((state) => state.currentTenant);
	return useQuery({
		queryKey: timelineKey(contentId),
		queryFn: ({ signal }) => {
			if (!contentId) throw new Error('Content ID is required');
			return blogCreationService.getGenerationTimeline(contentId, signal);
		},
		enabled: !!contentId && !!currentTenant && (options?.enabled !== false),
		staleTime: 30_000,
		gcTime: 300_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
};

// Feedback rows for all steps of a content item (all authors).
export const useGenerationFeedback = (
	contentId: string | null,
	options?: { enabled?: boolean },
) => {
	// No tenant → the service would throw (no test-tenant fallback); simply
	// don't fire the query until a tenant is selected.
	const currentTenant = useTenantStore((state) => state.currentTenant);
	return useQuery({
		queryKey: feedbackKey(contentId),
		queryFn: ({ signal }) => {
			if (!contentId) throw new Error('Content ID is required');
			return generationFeedbackService.list(contentId, signal);
		},
		enabled: !!contentId && !!currentTenant && (options?.enabled !== false),
		staleTime: 30_000,
		gcTime: 300_000,
		refetchOnWindowFocus: false,
		retry: 1,
	});
};

// Submit (upsert) step feedback with an optimistic cache update keyed by
// (step_index, author). `currentAuthor` identifies the caller's rows so a
// re-click replaces their previous verdict instead of appending.
export const useSubmitStepFeedback = (contentId: string | null, currentAuthor?: string) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: SubmitStepFeedbackData) => {
			if (!contentId) throw new Error('Content ID is required');
			return generationFeedbackService.submit(contentId, data);
		},
		onMutate: async (data) => {
			await queryClient.cancelQueries({ queryKey: feedbackKey(contentId) });
			const previous = queryClient.getQueryData<{ items: StepFeedback[] }>(
				feedbackKey(contentId),
			);

			const author = currentAuthor || 'me';
			const now = new Date().toISOString();
			const optimistic: StepFeedback = {
				id: `optimistic-${data.step_index}-${author}`,
				content_id: contentId || '',
				step_index: data.step_index,
				step_tool: data.step_tool ?? null,
				verdict: data.verdict,
				note: data.note ?? null,
				expected_output: data.expected_output ?? null,
				author,
				is_own: true,
				created_at: now,
				updated_at: now,
			};

			queryClient.setQueryData<{ items: StepFeedback[] }>(
				feedbackKey(contentId),
				(old) => {
					const items = old?.items ?? [];
					// Upsert the caller's row for this step — mirrors backend semantics.
					// Server rows carry is_own (JWT-derived); fall back to the author
					// email match for rows that predate the is_own contract.
					const rest = items.filter(
						(item) =>
							!(
								item.step_index === data.step_index &&
								(item.is_own === true || item.author === author)
							),
					);
					return { items: [...rest, optimistic] };
				},
			);

			return { previous };
		},
		onError: (error: Error, _data, context) => {
			if (context?.previous !== undefined) {
				queryClient.setQueryData(feedbackKey(contentId), context.previous);
			}
			toast.error(`Feedback failed: ${error.message}`);
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKey(contentId) });
		},
	});
};

// Remove a feedback row (e.g. retract a verdict).
export const useRemoveStepFeedback = (contentId: string | null) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (feedbackId: string) => {
			if (!contentId) throw new Error('Content ID is required');
			return generationFeedbackService.remove(contentId, feedbackId);
		},
		onMutate: async (feedbackId) => {
			await queryClient.cancelQueries({ queryKey: feedbackKey(contentId) });
			const previous = queryClient.getQueryData<{ items: StepFeedback[] }>(
				feedbackKey(contentId),
			);
			queryClient.setQueryData<{ items: StepFeedback[] }>(
				feedbackKey(contentId),
				(old) => ({ items: (old?.items ?? []).filter((item) => item.id !== feedbackId) }),
			);
			return { previous };
		},
		onError: (error: Error, _feedbackId, context) => {
			if (context?.previous !== undefined) {
				queryClient.setQueryData(feedbackKey(contentId), context.previous);
			}
			toast.error(`Failed to remove feedback: ${error.message}`);
		},
		onSettled: () => {
			queryClient.invalidateQueries({ queryKey: feedbackKey(contentId) });
		},
	});
};
